/**
 * BuyLeadsCard — Vendor / Venue / Pro / Entertainment-Pro dashboard widget
 * that exposes the admin-curated lead-pack catalog and the monthly bulk
 * "Lift Pass". Razorpay-checkout the purchase, then refresh local status.
 *
 * Backend:
 *   GET  /api/me/lead-packs/catalog
 *   GET  /api/me/lead-packs/status
 *   POST /api/me/lead-packs/create-order
 *   POST /api/me/lead-packs/verify-payment
 *   GET  /api/vendor/calendar/monthly-pass/status
 *   POST /api/vendor/calendar/monthly-pass/create-order
 *   POST /api/vendor/calendar/monthly-pass/verify-payment
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingBag, CalendarCheck, Lightning, CheckCircle, Spinner, Heart, Sparkle,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const BuyLeadsCard = () => {
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState(null);
  const [passStatus, setPassStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyingSku, setBuyingSku] = useState(null);
  const [buyingPass, setBuyingPass] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, ps] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/me/lead-packs/catalog`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/me/lead-packs/status`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendor/calendar/monthly-pass/status`, { withCredentials: true }),
      ]);
      setCatalog(c.data.catalog || []);
      setStatus(s.data || null);
      setPassStatus(ps.data || null);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || 'Could not load lead packs');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ---- Buy a lead pack ----------------------------------------------------
  const buyPack = async (sku, label, price) => {
    setBuyingSku(sku);
    try {
      const okSdk = await loadRazorpayScript();
      if (!okSdk) { toast.error('Could not load Razorpay'); return; }
      const { data: order } = await axios.post(
        `${BACKEND_URL}/api/me/lead-packs/create-order`,
        { sku }, { withCredentials: true },
      );
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'BeeWeddy',
        description: label,
        order_id: order.razorpay_order_id,
        handler: async (response) => {
          try {
            await axios.post(
              `${BACKEND_URL}/api/me/lead-packs/verify-payment`,
              {
                sku,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { withCredentials: true },
            );
            toast.success(`${order.qty} ${order.scope === 'wedding' ? 'wedding' : 'event'} leads added`);
            refresh();
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Verification failed');
          }
        },
        modal: { ondismiss: () => setBuyingSku(null) },
        theme: { color: '#C45A45' },
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
    } finally {
      setBuyingSku(null);
    }
  };

  // ---- Buy monthly lift pass ----------------------------------------------
  const buyMonthlyPass = async () => {
    setBuyingPass(true);
    try {
      const okSdk = await loadRazorpayScript();
      if (!okSdk) { toast.error('Could not load Razorpay'); return; }
      const { data: order } = await axios.post(
        `${BACKEND_URL}/api/vendor/calendar/monthly-pass/create-order`,
        {}, { withCredentials: true },
      );
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'BeeWeddy',
        description: `Monthly Lift Pass · ${order.month}`,
        order_id: order.razorpay_order_id,
        handler: async (response) => {
          try {
            await axios.post(
              `${BACKEND_URL}/api/vendor/calendar/monthly-pass/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { withCredentials: true },
            );
            toast.success(`Monthly Lift Pass active for ${order.month}`);
            refresh();
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Verification failed');
          }
        },
        modal: { ondismiss: () => setBuyingPass(false) },
        theme: { color: '#C45A45' },
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
    } finally {
      setBuyingPass(false);
    }
  };

  if (loading) return null;
  const weddingLeft = status?.extra_leads_remaining?.wedding ?? 0;
  const allLeft = status?.extra_leads_remaining?.all_events ?? 0;
  const weddingPacks = catalog.filter((c) => c.scope === 'wedding');
  const allPacks = catalog.filter((c) => c.scope === 'all_events');

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="buy-leads-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ShoppingBag size={20} className="text-brand-primary" weight="duotone" /> Buy Leads &amp; Lift Pass
        </CardTitle>
        <CardDescription>
          Already hit your monthly lead cap? Buy extra leads to keep your calendar open.
          Buy the <strong>Monthly Lift Pass</strong> to lift every busy day this month at one flat price.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Status row */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="buy-leads-status">
          <div className="border border-[#E8E1D5] p-3 text-center bg-stone-50">
            <div className="text-[10px] uppercase tracking-wider text-text-secondary flex items-center justify-center gap-1">
              <Heart size={11} weight="fill" className="text-rose-500" /> Wedding leads left
            </div>
            <div className="text-2xl font-bold text-brand-primary" data-testid="wedding-leads-left">{weddingLeft}</div>
          </div>
          <div className="border border-[#E8E1D5] p-3 text-center bg-stone-50">
            <div className="text-[10px] uppercase tracking-wider text-text-secondary flex items-center justify-center gap-1">
              <Sparkle size={11} weight="fill" className="text-purple-500" /> All-event leads left
            </div>
            <div className="text-2xl font-bold text-brand-primary" data-testid="all-event-leads-left">{allLeft}</div>
          </div>
          <div className="border border-[#E8E1D5] p-3 text-center bg-stone-50 col-span-2 md:col-span-1">
            <div className="text-[10px] uppercase tracking-wider text-text-secondary flex items-center justify-center gap-1">
              <CalendarCheck size={11} weight="fill" className="text-emerald-500" /> Lift Pass
            </div>
            <div className="text-sm font-bold mt-0.5">
              {passStatus?.active ? (
                <Badge className="bg-emerald-600 text-white">Active · {passStatus?.month}</Badge>
              ) : (
                <span className="text-text-secondary">Not active</span>
              )}
            </div>
          </div>
        </div>

        {/* Monthly Lift Pass CTA */}
        {!passStatus?.active && passStatus?.price_inr > 0 && (
          <div className="border-2 border-emerald-500 bg-emerald-50/40 p-3 flex items-center justify-between gap-3"
            data-testid="monthly-pass-cta">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Lightning size={16} weight="fill" className="text-emerald-700" />
                <strong className="text-sm">Monthly Lift Pass — {passStatus?.month}</strong>
              </div>
              <p className="text-xs text-text-secondary">
                One flat fee. Lift every system-busy date for the rest of this month — no per-day charge.
              </p>
            </div>
            <Button
              onClick={buyMonthlyPass}
              disabled={buyingPass}
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
              data-testid="buy-monthly-pass-btn"
            >
              {buyingPass
                ? <><Spinner size={14} className="mr-2 animate-spin" /> …</>
                : <>Buy · ₹{Number(passStatus.price_inr).toLocaleString('en-IN')}</>}
            </Button>
          </div>
        )}

        {/* Lead pack catalog */}
        {[['Wedding Lead Packs', weddingPacks, 'rose'], ['All Event Lead Packs', allPacks, 'purple']]
          .filter(([, list]) => list.length > 0)
          .map(([title, list, tone]) => (
            <div key={title} data-testid={`lead-pack-group-${tone}`}>
              <h4 className="text-xs uppercase tracking-wider font-bold text-text-secondary mb-2 flex items-center gap-1.5">
                {tone === 'rose'
                  ? <Heart size={12} weight="fill" className="text-rose-500" />
                  : <Sparkle size={12} weight="fill" className="text-purple-500" />}
                {title}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {list.map((p) => (
                  <div key={p.sku}
                    className={`border-2 p-3 ${tone === 'rose' ? 'border-rose-200 hover:border-rose-400' : 'border-purple-200 hover:border-purple-400'} transition`}
                    data-testid={`lead-pack-${p.sku}`}>
                    <div className="text-3xl font-bold leading-none">
                      {p.qty}
                      <span className="text-xs font-normal text-text-secondary ml-1">leads</span>
                    </div>
                    <div className="text-xs text-text-secondary mt-0.5">{p.label}</div>
                    <Button
                      onClick={() => buyPack(p.sku, p.label, p.price_inr)}
                      disabled={buyingSku === p.sku}
                      size="sm"
                      className={`w-full mt-3 rounded-none text-white ${tone === 'rose' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                      data-testid={`buy-pack-${p.sku}-btn`}
                    >
                      {buyingSku === p.sku
                        ? <><Spinner size={14} className="mr-2 animate-spin" /> …</>
                        : <>Buy · ₹{Number(p.price_inr).toLocaleString('en-IN')}</>}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}

        {catalog.length === 0 && (
          <p className="text-sm text-text-secondary text-center py-2">
            No lead packs are available right now. Check back soon.
          </p>
        )}

        {/* Purchase history (last 5) */}
        {(status?.history || []).slice(0, 5).length > 0 && (
          <div className="pt-2 border-t border-[#E8E1D5]" data-testid="lead-pack-history">
            <h4 className="text-xs uppercase tracking-wider font-bold text-text-secondary mb-2">Recent purchases</h4>
            <ul className="text-xs space-y-1">
              {(status.history || []).slice(0, 5).map((h, idx) => (
                <li key={idx} className="flex items-center justify-between gap-2 text-text-secondary">
                  <span>
                    <CheckCircle size={12} className="inline mr-1 text-emerald-600" weight="fill" />
                    {h.qty} {h.scope === 'wedding' ? 'wedding' : 'event'} leads
                  </span>
                  <span>
                    {h.qty_consumed}/{h.qty} used ·
                    valid until {new Date(h.valid_to).toLocaleDateString('en-IN')}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BuyLeadsCard;
